import * as Immutable from 'immutable'
import * as React from 'react'
import { IntlProvider } from 'react-intl'
import {
  Action,
  any,
  async,
  AsyncState,
  fromJSX,
  loadingAsyncState,
  none,
  nothing,
  Option,
  some,
  stateful,
  Widget
} from 'widgets-for-react'
import { LoaderCircle } from '../../accommodations/models/loading'
import { getMerchant } from '../../api/online_payment_platform'
import { getIdealIssuers, IdealIssuerResponse } from '../../api/payments'
import { MerchantResponse } from '../../types/online_payment_platform'
import { IdealIssuer } from '../../types/types'
import * as DL from '../../utils/datalayer'
import {
  ChooseOptionComponent,
  ChooseOptionProps
} from '../shared/choose_option'
import { bankAccountRequirement } from './onboarding'

type Props = {
  id: number
  idealImage: string
}

type VerificationMethodKey = 'ideal' | 'other'

type State = {
  idealIssuers: AsyncState<IdealIssuerResponse>
  merchantResponse: AsyncState<MerchantResponse>
  selectedMethod: 'none' | VerificationMethodKey
  issuerSelected: string
}
type VerificationMethod = {
  id: number
  name: VerificationMethodKey
  label: string
  extraSelected: string
}

const methods = (issuerSelected: string): VerificationMethod[] => [
  {
    id: 0,
    name: 'ideal',
    label: I18n.t('users.opp_onboarding.account_verification.ideal'),
    extraSelected: issuerSelected
  },
  {
    id: 1,
    name: 'other',
    label: I18n.t('users.opp_onboarding.account_verification.other'),
    extraSelected: ''
  }
]

const initialState = (props: Props): State => ({
  idealIssuers: loadingAsyncState(_ => getIdealIssuers()),
  merchantResponse: loadingAsyncState(_ => getMerchant(props.id)),
  selectedMethod: 'none',
  issuerSelected: ''
})

const getErrorFromState = (s0: State): Option<string> => {
  if (
    s0.merchantResponse.kind === 'loaded' &&
    !bankAccountRequirement(s0.merchantResponse.value)
  ) {
    return some(
      I18n.t('users.opp_onboarding.account_verification.no_verification_needed')
    )
  }

  if (
    ['connection-error', 'unauthorized', 'failed'].includes(
      s0.merchantResponse.kind
    )
  ) {
    return some(
      I18n.t('users.opp_onboarding.account_verification.generic_error')
    )
  }
  return none()
}

const chooseOptionProps = (
  props: Props,
  s0: State,
  setState: (_: Action<State>) => void,
  idealIssuers: IdealIssuer[]
): ChooseOptionProps<VerificationMethod> => ({
  selected: s0.selectedMethod,
  title: I18n.t('users.opp_onboarding.account_verification.title'),
  intro: I18n.t('users.opp_onboarding.account_verification.intro'),
  options: methods(s0.issuerSelected),
  submitLabel: I18n.t('users.opp_onboarding.account_verification.submit_label'),
  emptyExtraOptionLabel: I18n.t(
    'users.opp_onboarding.account_verification.empty_extra'
  ),
  error: getErrorFromState(s0),
  selectThis: (ii: VerificationMethodKey) =>
    setState(s => ({ ...s, selectedMethod: ii })),
  selectThisExtraFor: (_, issuerSelected: string) =>
    setState(s => ({ ...s, issuerSelected })),
  onSubmit: () => {
    if (s0.merchantResponse.kind === 'loaded') {
      const bank = bankAccountRequirement(s0.merchantResponse.value)
      const selectedIssuer = idealIssuers.find(
        ii => ii.name === s0.issuerSelected
      )
      if (!bank || (s0.selectedMethod === 'ideal' && !selectedIssuer)) {
        setState(s => ({ ...s, errors: true }))
      } else {
        DL.push({
          event: 'OPP',
          category: 'Onboarding OPP',
          action: 'Link bankaccount',
          label: s0.selectedMethod
        })
        window.location.href =
          bank.object_redirect_url +
          (s0.selectedMethod === 'ideal'
            ? `?payment_method=ideal&issuer=${selectedIssuer.bic}`
            : '') // this goes to the opp whitelabel page
        // : '?payment_method=bcmc') // note: this goes seamlessly to a credit card verify page, but it doesn't seem to change status in the compliance
      }
    }
  },
  formDisabled:
    s0.merchantResponse.kind === 'loaded' &&
    !bankAccountRequirement(s0.merchantResponse.value),
  submitDisabled:
    s0.merchantResponse.kind !== 'loaded' ||
    s0.selectedMethod === 'none' ||
    (s0.selectedMethod === 'ideal' && s0.issuerSelected === ''),
  images: Immutable.Map({
    ideal: props.idealImage
  }),
  extraSelections: Immutable.Map({
    ideal: idealIssuers.map((ii, i) => ({
      id: i,
      name: ii.name,
      label: ii.name,
      extraSelected: ''
    }))
  })
})

const merchantLoader = (s0: State): Widget<Action<State>> =>
  async<MerchantResponse>()(s0.merchantResponse).map(uc => s => {
    const merchantResponse = uc(s.merchantResponse)
    return {
      ...s,
      merchantResponse
    }
  })

const issuersLoader = (s0: State): Widget<Action<State>> =>
  async<IdealIssuerResponse>(_ => nothing())(s0.idealIssuers).map(
    updateIssuers => s => ({
      ...s,
      idealIssuers: updateIssuers(s.idealIssuers)
    })
  )

const chooseOptionWidget = (props: Props, s0: State): Widget<Action<State>> =>
  fromJSX(setState => (
    <div className="page-section page-section--pt">
      {s0.idealIssuers.kind === 'loaded' ? (
        <ChooseOptionComponent<VerificationMethod>
          {...chooseOptionProps(
            props,
            s0,
            setState,
            s0.idealIssuers.value.ideal_issuers
          )}
        />
      ) : (
        <LoaderCircle />
      )}
    </div>
  ))

export class AccountVerification extends React.Component<Props> {
  render() {
    return (
      <>
        {stateful<State>({ key: 'account_verification' })(s0 =>
          any<Action<State>>()([
            merchantLoader(s0),
            issuersLoader(s0),
            chooseOptionWidget(this.props, s0)
          ]).map(a => a(s0))
        )(initialState(this.props)).run(_ => {})}
      </>
    )
  }
}

export default (props: Props): JSX.Element => (
  <IntlProvider locale={window.Gites.locale}>
    <AccountVerification {...props} />
  </IntlProvider>
)
